import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiDocsService, ApiEndpointDoc } from '../../services/api-docs.service';
import { ConnectionService } from '../../services/connection.service';

interface ApiEndpointGroup {
  service: string;
  endpoints: ApiEndpointDoc[];
}

interface ApiDocsDialogData {
  title?: string;
  services?: string[];
  paths?: string[];
}

@Component({
  selector: 'app-api-docs-dialog',
  templateUrl: './api-docs.dialog.html',
  styleUrls: ['./api-docs.dialog.scss']
})
export class ApiDocsDialogComponent implements OnInit {

  isLoading = true;
  error = "";
  query = "";
  groups: ApiEndpointGroup[] = [];
  filteredGroups: ApiEndpointGroup[] = [];
  selectedEndpoint: ApiEndpointDoc | null = null;
  visibleEndpoints: ApiEndpointDoc[] = [];
  copied = false;
  serverUrl = "SERVER_URL";
  apiKey = "YOUR_API_KEY";

  constructor(
    private apiDocsService: ApiDocsService,
    private connectionService: ConnectionService,
    private clipboard: Clipboard,
    @Inject(MAT_DIALOG_DATA) public data: ApiDocsDialogData) {}

  ngOnInit(): void {
    this.apiDocsService.list().subscribe({
      next: response => {
        this.serverUrl = response?.serverUrl || this.connectionService.address || "SERVER_URL";
        this.apiKey = response?.apiKey || "YOUR_API_KEY";
        const endpoints = this.scopeEndpoints(response?.endpoints || []);
        this.visibleEndpoints = endpoints;
        this.groups = this.groupEndpoints(endpoints);
        this.filteredGroups = this.groups;
        this.selectedEndpoint = endpoints.find(endpoint => endpoint.path === '/website-enquiry-service/create') || endpoints[0] || null;
        this.isLoading = false;
      },
      error: () => {
        this.error = "Could not load API documentation.";
        this.isLoading = false;
      }
    });
  }

  selectEndpoint(endpoint: ApiEndpointDoc) {
    this.selectedEndpoint = endpoint;
  }

  search() {
    const query = this.query.trim().toLowerCase();
    if (!query) {
      this.filteredGroups = this.groups;
      this.visibleEndpoints = this.groups.flatMap(group => group.endpoints);
      return;
    }

    this.filteredGroups = this.groups
      .map(group => ({
        service: group.service,
        endpoints: group.endpoints.filter(endpoint => {
          return [
            endpoint.method,
            endpoint.path,
            endpoint.service,
            endpoint.auth,
            endpoint.summary
          ].filter(value => !!value).join(" ").toLowerCase().includes(query);
        })
      }))
      .filter(group => group.endpoints.length > 0);
    this.visibleEndpoints = this.filteredGroups.flatMap(group => group.endpoints);
  }

  stringify(value: any): string {
    return JSON.stringify(value, null, 2);
  }

  getCurl(endpoint: ApiEndpointDoc): string {
    const body = endpoint.requestExample !== undefined
      ? ` \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(endpoint.requestExample)}'`
      : "";
    const auth = endpoint.auth === 'authenticated'
      ? ` \\\n  -H "authtoken: YOUR_TOKEN"`
      : endpoint.auth === 'apiKey'
      ? ` \\\n  -H "x-api-key: ${this.apiKey}"`
      : "";

    return `curl -X ${endpoint.method} "${this.serverUrl}${endpoint.path}"${auth}${body}`;
  }

  copyVisibleEndpoints() {
    const payload = {
      title: this.data?.title || "API endpoints",
      endpoints: this.visibleEndpoints.map(endpoint => ({
        method: endpoint.method,
        path: endpoint.path,
        auth: endpoint.auth,
        headers: this.getAuthHeaders(endpoint),
        summary: endpoint.summary || "",
        requestExample: endpoint.requestExample,
        responseExample: endpoint.responseExample
      }))
    };

    this.copied = this.clipboard.copy(JSON.stringify(payload, null, 2));
    if (this.copied) {
      setTimeout(() => this.copied = false, 2000);
    }
  }

  private scopeEndpoints(endpoints: ApiEndpointDoc[]): ApiEndpointDoc[] {
    const services = this.data?.services || [];
    const paths = this.data?.paths || [];

    if (!services.length && !paths.length) {
      return endpoints;
    }

    return endpoints.filter(endpoint => {
      return services.includes(endpoint.service) || paths.includes(endpoint.path);
    });
  }

  private getAuthHeaders(endpoint: ApiEndpointDoc): any {
    if (endpoint.auth === 'authenticated') {
      return {
        authtoken: "YOUR_TOKEN"
      };
    }

    if (endpoint.auth === 'apiKey') {
      return {
        "x-api-key": this.apiKey
      };
    }

    return {};
  }

  private groupEndpoints(endpoints: ApiEndpointDoc[]): ApiEndpointGroup[] {
    const groups = new Map<string, ApiEndpointDoc[]>();

    for (const endpoint of endpoints) {
      const list = groups.get(endpoint.service) || [];
      list.push(endpoint);
      groups.set(endpoint.service, list);
    }

    return Array.from(groups.entries()).map(([service, groupedEndpoints]) => ({
      service,
      endpoints: groupedEndpoints
    }));
  }
}
